import { useMemo } from 'react';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import { z } from 'zod';
import { CursorListSchema, type CursorList as CursorListEnvelope } from '../api/list.gen';

// Shared cursor-list hook (DOD §4 G-18, story 2-76). The CLIENT half of the
// `contracts/list.js` envelope: one hook every list page uses, so paging,
// "has more", totals and loading states are decided once instead of per page.
//
// Why the generic lives here and not in the contract: the generator walks a
// documented zod SUBSET and emits CONCRETE types per endpoint — it has no
// notion of `CursorList<T>`, and `z.discriminatedUnion` (the usual escape) is
// explicitly outside the subset. So the wire contract stays concrete
// (`contracts/list.js`'s `cursorList(Property)` → `{ items: Property[]; … }`)
// and the parameterisation happens in TypeScript, where it is free. Both the
// type and the runtime schema below are DERIVED from the generated envelope —
// nothing about the shape is re-declared here, so a contract change is a
// compile error, not a drift.

/** The envelope, parameterised by its item type. Derived from the generated `CursorList`. */
export type CursorList<T> = Omit<CursorListEnvelope, 'items'> & { items: T[] };

/**
 * The envelope's RUNTIME schema, parameterised by an item schema.
 * `CursorListSchema.extend({ items })` — composition of the generated contract
 * schema (G-26's "composition is allowed; re-declaration is not"), so
 * `nextCursor`/`total`/`.strict()` can only ever come from the contract.
 */
export function cursorListOf<TItem extends z.ZodTypeAny>(item: TItem) {
  return CursorListSchema.extend({ items: z.array(item) });
}

export interface CursorPageParams {
  cursor?: string | undefined;
  limit: number;
}

/**
 * The envelope a `fetchPage` resolves to.
 *
 * Defaults to the bare G-18 shape. An endpoint may COMPOSE extra keys onto that
 * shape (`cursorList(Item).extend({ view })` in `contracts/*.js`, exactly as
 * `ListQuery.extend()` composes a request), and such a caller parameterises this
 * so the extra keys survive into `page` below with their real types. Story
 * 5-2b's My day is the first: its `view` descriptor is a fact about the ANSWER
 * (which mode was served, in which timezone, and whether the team view is even
 * offerable), not about any row, so repeating it on every item would be both
 * redundant and unable to speak for an EMPTY page.
 */
export interface UseCursorListOptions<T, TPage extends CursorList<T> = CursorList<T>> {
  /** From the module's query-key factory (`<module>Keys.list(...)`) — never an inline array (G-19). */
  queryKey: QueryKey;
  /** Usually a generated client function: `(params) => listProperties(params)`. */
  fetchPage: (params: CursorPageParams) => Promise<TPage>;
  /** Page size. The contract caps this at 100 (`contracts/list.js` MAX_LIMIT). */
  limit?: number;
  enabled?: boolean;
  /**
   * Optional: validate each page against `cursorListOf(itemSchema)` at runtime.
   * OFF by default and deliberately so — the envelope's item schemas are
   * `.strict()`, so a server that adds a field before the client is redeployed
   * would blank the page rather than render a superset. Opt in where a hard
   * failure is the point (the /app/design proof does).
   */
  itemSchema?: z.ZodType<T, z.ZodTypeDef, unknown>;
  /**
   * Freshness overrides, passed straight through to react-query (story 3-3b).
   * The app default is `staleTime: 60s, refetchOnWindowFocus: false` — right
   * for a list PAGE, wrong for a list whose rows are an AUTHORIZATION answer
   * that another session may have just changed (the TypePicker: a type
   * deactivated, or a role revoked, in one context must stop being offered in
   * another without a page reload). Omitted → the client defaults, so every
   * existing caller is byte-identical.
   */
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  /**
   * Poll interval in ms (story 5-2b). For a list whose rows can be changed
   * OUT FROM UNDER the viewer by somebody else — an inspection reassigned away
   * while the field worker is looking at their day (P05 §8's live-change
   * clause) — and where the change must EXPLAIN itself rather than be noticed
   * on the next manual reload. Omitted → no polling, so every existing caller
   * is byte-identical.
   */
  refetchInterval?: number;
}

export interface UseCursorListResult<T, TPage extends CursorList<T> = CursorList<T>> {
  /** Every page loaded so far, flattened, in server order. */
  items: T[];
  /**
   * The FRESHEST page envelope, whole — for endpoints that compose extra keys
   * onto the G-18 shape. `undefined` until the first page resolves.
   *
   * The freshest rather than the first, for the same reason `total` reads the
   * last page: a composed descriptor describes the answer as it is NOW, and a
   * "load more" that arrived after a mode change must not be described by the
   * page that preceded it.
   */
  page: TPage | undefined;
  /** Size of the full filtered collection when the endpoint reports it (`total?`). */
  total: number | undefined;
  /**
   * How many pages have been loaded so far (0 before the first page resolves,
   * 1 after it, 2 after one "Load more", …). Story 2-139: the URL-owned page
   * count a collection restores on reload/back (G-25) drives fetches by
   * comparing against this — the caller's own effect calls `fetchNextPage()`
   * until `pageCount` catches up to the URL's count, rather than the hook
   * reaching into react-query's cache itself.
   */
  pageCount: number;
  /** The server issued a `nextCursor` for the last page loaded. */
  hasMore: boolean;
  fetchNextPage: () => void;
  /** First page in flight (no items yet). */
  isLoading: boolean;
  /** A subsequent page in flight (items already on screen). */
  isLoadingMore: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  /**
   * When the data on screen was last successfully fetched (epoch ms; 0 before
   * the first success). The honest input for a cache-age label — DOD-wise this
   * is P05 §8's "Cached/stale data is labelled with age", and the age has to be
   * the age of the DATA, not of the component.
   */
  dataUpdatedAt: number;
  /** A request is in flight (any page, including a background refetch). */
  isFetching: boolean;
  /**
   * react-query's fetch status. `'paused'` is the OFFLINE state: the query is
   * enabled and wants to run, and the browser has no network — which is exactly
   * when a surface owes the user "this is a cached copy" rather than a spinner
   * that never resolves.
   */
  fetchStatus: 'fetching' | 'paused' | 'idle';
}

const DEFAULT_LIMIT = 50;

export function useCursorList<T, TPage extends CursorList<T> = CursorList<T>>(
  options: UseCursorListOptions<T, TPage>
): UseCursorListResult<T, TPage> {
  const {
    queryKey,
    fetchPage,
    limit = DEFAULT_LIMIT,
    enabled = true,
    itemSchema,
    staleTime,
    refetchOnWindowFocus,
    refetchInterval,
  } = options;

  // The cache entry an INFINITE query writes is `{ pages, pageParams }`, not the
  // page object a plain `useQuery` on the same key would write. Sharing a key
  // between the two is therefore a real bug — the plain reader gets the wrong
  // SHAPE and crashes on `.items` (this is not hypothetical: it took down the
  // now-retired `OnboardingTour` (story 2-106) the moment PropertiesPage
  // adopted the hook while three other call sites still read the same
  // `propertiesKeys.list(...)` key). Appending a
  // marker keeps the two caches separate while staying UNDER the module's key
  // prefix, so `invalidateQueries({ queryKey: xKeys.lists() })` after a mutation
  // still reaches this list. `limit` is part of the key because a different page
  // size is a different pagination, not the same data.
  const infiniteKey = [...queryKey, 'cursor', { limit }];

  const query = useInfiniteQuery({
    queryKey: infiniteKey,
    queryFn: async ({ pageParam }) => {
      // exactOptionalPropertyTypes: build the params object without ever
      // assigning an explicit `undefined` to `cursor`.
      const page = await fetchPage(pageParam ? { cursor: pageParam, limit } : { limit });
      if (!itemSchema) return page;
      // Parsed for VALIDATION only; the parsed value is discarded so composed
      // envelope keys (which the item-level schema knows nothing about) are not
      // stripped out of the page the caller reads.
      cursorListOf(itemSchema).parse(page);
      return page;
    },
    // `null` (not undefined) is the documented terminal value on the wire, and
    // react-query treats a null/undefined next-page param as "no more pages".
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: TPage) => lastPage.nextCursor,
    enabled,
    // Spread, not `staleTime: staleTime` — under exactOptionalPropertyTypes an
    // explicit `undefined` is NOT the same as "absent", and passing one would
    // override react-query's own default instead of deferring to it.
    ...(staleTime === undefined ? {} : { staleTime }),
    ...(refetchOnWindowFocus === undefined ? {} : { refetchOnWindowFocus }),
    ...(refetchInterval === undefined ? {} : { refetchInterval }),
  });

  const pages = query.data?.pages;

  const items = useMemo(() => (pages ?? []).flatMap((page) => page.items), [pages]);

  const latest = pages && pages.length > 0 ? pages[pages.length - 1] : undefined;

  return {
    items,
    page: latest,
    // The freshest page carries the freshest count.
    total: latest?.total,
    pageCount: pages?.length ?? 0,
    hasMore: query.hasNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
    dataUpdatedAt: query.dataUpdatedAt,
    isFetching: query.isFetching,
    fetchStatus: query.fetchStatus,
  };
}
